import AddIcon from "@mui/icons-material/Add";
import { Button } from "@mui/material";
import { useMemo, useState } from "react";

import { useCurrentUser } from "Auth/Hooks/useCurrentUser";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { useSuperiorPartnersQuery } from "Partners/Api/Queries/useSuperiorPartnersQuery";
import type { PartnerPrefillData } from "Partners/Forms/PartnerForm";
import { CreatePartnerModal } from "./CreatePartnerModal";

interface NewPartnerButtonProps {
  parentPartnerID?: string;
  prefill?: PartnerPrefillData;
  autoOpen?: boolean;
}

export const NewPartnerButton = ({
  parentPartnerID,
  prefill,
  autoOpen = false,
}: NewPartnerButtonProps): React.ReactNode => {
  const { t } = useResource();
  const { isSysAdmin, user } = useCurrentUser();
  const [isModalOpen, setIsModalOpen] = useState(autoOpen);
  // Only SysAdmin can call supervisor-search endpoint; partner role gets 403
  const { data: superiorPartnersData } = useSuperiorPartnersQuery(isSysAdmin);

  const superiorPartnerOptions = useMemo(() => {
    if (isSysAdmin) {
      return (superiorPartnersData?.items ?? []).map((p) => ({
        value: p.partnerID,
        label: p.companyName,
      }));
    }
    // Partner role: lock dropdown to current user's partner
    if (user?.partnerID && user?.partnerName) {
      return [{ value: user.partnerID, label: user.partnerName }];
    }
    return [];
  }, [isSysAdmin, superiorPartnersData, user]);

  return (
    <>
      <Button
        variant="contained"
        startIcon={<AddIcon />}
        onClick={() => setIsModalOpen(true)}
        sx={{ px: Spacing.L }}
      >
        {parentPartnerID
          ? t(Resources.Partners.AddSubPartnerButton)
          : t(Resources.Common.NewPartner)}
      </Button>
      <CreatePartnerModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        superiorPartnerOptions={superiorPartnerOptions}
        parentPartnerID={parentPartnerID}
        prefill={prefill}
      />
    </>
  );
};
